import * as React from 'react';
import * as moment from 'moment-timezone';
import {Bundle, BundleData, IBundle} from "data/BundleData";
import {observer} from 'mobx-react';
import {action, computed} from 'mobx';
import {FitBitIcon} from '../widgets/icons';
import {UIState} from 'data/UIState';
import {MainStageData} from "data/MainStageData";
import {NavLink} from "components/nav/NavLink";
import {PageEnums} from "components/nav/PageEnums";

import './Nav.pcss';
import {User} from "data/entities/User";
import {ISolution} from "../../data/SolutionDataSource";

interface Props {
	hide?:boolean;
	user:User;
}

@observer
export class Nav extends React.Component<Props> {

	handleSelectCustomPage = (customPage) => {
		UIState.currentCustomPageKey = customPage.key;
		UIState.currentPage = PageEnums.custom;
	}

	@computed get lastFeedUpdate() {
		return UIState.feedData.lastRefresh ? 'as of '+UIState.feedData.lastRefresh.tz(moment.tz.guess()).format('h:mma') : '';
	}

	@computed get mainStageLiveStyle() {
		return MainStageData.liveMainStageEvent ? { color:'white', fontWeight:'bold', backgroundColor:'#ed5a5a', borderRadius:'11px', padding:'0 14px', fontSize:'14px'} : {};
	}

	@computed get mainStageLivePillValue() {
		return  MainStageData.liveMainStageEvent ? 'LIVE' :'';
	}

	@action goToDashboard = () => {
		UIState.currentPage = PageEnums.dashboard;
	}

	render() {
		if (!UIState.feedData.feedLoaded) {
			return null;
		} else {
			const solution = UIState.feedData.solution;

			return (
				<div>
					<div className={`nav${this.props.hide ? ' hide' : ''}`}>

						<img className="logo" src={solution.solutionLogo} alt={`Logo for ${solution.name}`}

						/>

						<a href="#maincontent" className="skip-to-main-content-link">Skip to main content</a>

						<nav aria-label="Main navigation">
						<ul className={"reset"}>

							<li>
								<div className="back-to-dash">
									<button aria-label={'Back to Dashboard'} onClick={this.goToDashboard}>
										<span className={"lab"}>« Dashboard</span>
									</button>
								</div>
							</li>


						<NavLink
							text={UIState.isHealthFairMode ? "Event Feed" : "Feed"}
							pageEnum={PageEnums.feedLandingPage}
							infoPillValue={this.lastFeedUpdate}
							infoPillReadable={". "+this.lastFeedUpdate}
							pillClass="info"
						/>

						<NavLink
							pageEnum={PageEnums.mainstage}
							pillStyle={this.mainStageLiveStyle}
							infoPillValue={this.mainStageLivePillValue}
							hide={!MainStageData.mainStageEventsEnabled}
						/>

						{UIState.feedData.solution.prizes.length>0 &&
							<NavLink
								pillClass={"points"}
								pageEnum={PageEnums.prizes}
								text={solution.prizesPageName ? solution.prizesPageName : "Prizes"}
								infoPillValue={UIState.feedData.pointsDataSource.points + ' pts'}
								infoPillReadable={'You currently have '+UIState.feedData.pointsDataSource.points + ' points'}
							/>
						}

						<BundleNavigation solution={solution} />

						<h2>Resources</h2>

						{solution.customPages.map((customPage) => <NavLink
							key={customPage.key}
							small
							selected={UIState.currentCustomPage && UIState.currentCustomPage.key === customPage.key}
							onClick={() => {
								this.handleSelectCustomPage(customPage);
							}}
							text={customPage.navName}
						/>)}

							{BundleData.zoomAppointmentBundles.map((bundle, idx)=>{
								return <BundleGroupNavLink key={bundle._id} bundle={bundle} onSelectBundle={()=>{
									BundleData.selectedBundleId = bundle._id;
									UIState.currentPage = PageEnums.booth;
								}} />
							})}

						<NavLink small pageEnum={PageEnums.myCoaching} hide={!UIState.settings.ENABLE_MY_COACHING}/>
						<NavLink small pageEnum={PageEnums.shopwell} hide={!UIState.settings.ENABLE_SHOPWELL}/>
						<NavLink small pageEnum={PageEnums.foodswitch} hide={!UIState.settings.ENABLE_FOODSWITCH}/>
						<NavLink small pageEnum={PageEnums.calm} hide={!UIState.settings.ENABLE_CALM}/>
						<NavLink small pageEnum={PageEnums.headspace} hide={!UIState.settings.ENABLE_HEADSPACE}/>
						<NavLink small pageEnum={PageEnums.quitnow} hide={!UIState.settings.ENABLE_QUITNOW}/>
						<NavLink small pageEnum={PageEnums.biometrics} hide={!UIState.settings.ENABLE_BIOMETRICS}/>

						<NavLink small pageEnum={PageEnums.library}/>


						{this.props.user.fitbitEnabled &&
							<>
								<div style={{paddingLeft: '16px', lineHeight: '16px', display: 'flex'}}>
									<FitBitIcon style={{width: '16px', height: '16px', marginRight: '8px'}} color='#3f3f3f'/>
									<div style={{
										flexGrow: 1,
										display: 'inline-block',
										lineHeight: '16px',
										fontFamily: 'Roboto',
										fontStyle: 'italic',
										fontSize: '14px',
										color: '#3f3f3f'
									}}>Fitbit connected
									</div>
								</div>
							</>
						}

						{UIState.isHealthFairMode && this.props.user.isAdmin &&
							<>
								<NavLink small pageEnum={PageEnums.mainstageQuestions}/>
							</>
						}

						</ul>
						</nav>


					</div>
					<div aria-hidden={true} id="LatentBackdrop" className={`${this.props.hide ? 'hide' : ''}`}
							 onTouchEnd={UIState.hideNav} onClick={UIState.hideNav}></div>
				</div>
			)
		}
	}
}



const BundleNavigation = observer((props:{solution:ISolution}) => {

	// no bundles available to render
	if (!BundleData.bundlesEnabled) {
		return null;
	}

	const handleSelectBundle = (bundle: Bundle) => {
		BundleData.selectedBundleId = bundle._id;
		UIState.currentPage = PageEnums.booth;
	}

	const booths = <BundleGroup title={"BOOTHS"} bundles={BundleData.booths} onSelectBundle={handleSelectBundle} />;
	const vendorBooths = <BundleGroup title={props.solution.vendorBoothsNavLabel} bundles={BundleData.vendorBooths} onSelectBundle={handleSelectBundle} />;

	return (
		<>
			<BundleGroup title={"Webinars"} bundles={BundleData.webinars} onSelectBundle={handleSelectBundle} />
			{props.solution.vendorBoothsDisplayFirst ?
				<>{vendorBooths}{booths}</>
			:
				<>{booths}{vendorBooths}</>
			}
		</>
	);
});


const BundleGroup = observer((props:{ title:string; bundles:Bundle[]; onSelectBundle:(bundle:Bundle)=>void; }) => {
	if(0===props.bundles.length) {
		return null;
	}
	return (
		<>
			<h2 aria-hidden={true}>{props.title}</h2>
			{props.bundles.map((bundle: Bundle) => <BundleGroupNavLink key={bundle._id} bundle={bundle} onSelectBundle={props.onSelectBundle} />)}
		</>
	);
});

const BundleGroupNavLink = observer((props:{ bundle:Bundle; onSelectBundle:(bundle:Bundle)=>void;  }) => {
	const onClick = () => { props.onSelectBundle(props.bundle); }
	const hiddenReadableTextLabel = BundleData.niceTypeForBundleType(props.bundle.type);

	let pillStyle:any = null;
	let infoPillValue = '';
	let infoPillReadable = '';

	if(props.bundle.live) {
		pillStyle = { color:'white', fontWeight:'bold', backgroundColor:'#ed5a5a', borderRadius:'11px', padding:'0 14px', fontSize:'14px'};
		infoPillValue = 'LIVE';
		infoPillReadable = 'This ' + props.bundle.niceType + ' is currently live.';
	}

	return (
				<NavLink
					key={props.bundle._id} small
					selected={BundleData.selectedBundleId === props.bundle._id}
					onClick={onClick}
					text={props.bundle.navName}
					hiddenReadableTextLabel={hiddenReadableTextLabel}
					pillStyle={pillStyle}
					infoPillValue={infoPillValue}
					infoPillReadable={infoPillReadable}
				/>
	);
})
